import { t } from "@lingui/macro";
import { captureEvent } from "@sentry/react";
import {
  DeleteProjectDocument,
  type DeleteProjectMutation,
  type DeleteProjectMutationVariables,
  ProjectOrderByColumnEnum,
  ProjectsWhereColumn,
  type ProjectTemplateFragment,
  ProjectTemplatesDocument,
  ProjectTemplatesFilterOptionsDocument,
  type ProjectTemplatesFilterOptionsQuery,
  type ProjectTemplatesFilterOptionsQueryVariables,
  type ProjectTemplatesQuery,
  type ProjectTemplatesQueryVariables,
  ProjectTypeEnum,
  SortOrder,
  SqlOperator,
} from "@src/__generated__/graphql";
import { ProjectCategorySelectBottomExtraContent } from "@src/components/ui-kit/ProjectCategorySelect";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import type { AppStore } from "@src/stores/AppStore";
import { Filter, Filters } from "@src/utils/components/filters/models";
import { OrderBy } from "@src/utils/components/sorting/OrderBy";
import mapToOptions from "@src/utils/map-to-options";
import { PaginationState } from "@src/utils/mobx/states/PaginationState";
import { getParamFromQuery } from "@src/utils/url";
import { action, computed, makeObservable, observable } from "mobx";
import Router from "next/router";
import { getBillingCategoryOptions } from "../../projects/list/Store";

export class ProjectTemplatesStore {
  readonly storeKey = "project-templates";

  fetcher = new FetchHelper<
    ProjectTemplatesQuery,
    ProjectTemplatesQueryVariables
  >(ProjectTemplatesDocument);
  filterOptionsFetcher = new FetchHelper<
    ProjectTemplatesFilterOptionsQuery,
    ProjectTemplatesFilterOptionsQueryVariables
  >(ProjectTemplatesFilterOptionsDocument);
  deleteMutator = new MutationHelper<
    DeleteProjectMutation,
    DeleteProjectMutationVariables
  >(DeleteProjectDocument);

  orderBy = new OrderBy<ProjectOrderByColumnEnum>([
    {
      column: ProjectOrderByColumnEnum.ProjectCode,
      order: SortOrder.Asc,
    },
  ]);
  pagination = new PaginationState(this.storeKey, {
    onChangePagination: (pagination) =>
      this.fetchTableData({
        first: pagination.asParams.first,
        page: pagination.asParams.page,
      }),
  });
  where = new Filters(
    [
      new Filter({
        column: ProjectsWhereColumn.BrandId,
        operator: SqlOperator.In,
        title: t`Client/Brand`,
        tagTitle: t`Brand`,
        options: [],
        sectioned: true,
        hasSelectAllOption: true,
      }),
      new Filter({
        column: ProjectsWhereColumn.ProjectManagerId,
        operator: SqlOperator.In,
        title: t`Project Manager`,
        options: [],
        hasSelectAllOption: true,
      }),
      new Filter({
        column: ProjectsWhereColumn.OtherProjectManagerId,
        operator: SqlOperator.In,
        title: t`Other Project Manager`,
        options: [],
        hasSelectAllOption: true,
      }),
      new Filter({
        column: ProjectsWhereColumn.TeamId,
        operator: SqlOperator.In,
        title: t`Team`,
        options: [],
      }),
      new Filter({
        column: ProjectsWhereColumn.Billable,
        operator: SqlOperator.Eq,
        title: t`Billing category`,
        options: getBillingCategoryOptions(),
      }),
      new Filter({
        column: ProjectsWhereColumn.Type,
        operator: SqlOperator.Eq,
        title: t`Type`,
        options: [
          {
            value: ProjectTypeEnum.FixedPrice,
            label: t`Fixed-price`,
          },
          {
            value: ProjectTypeEnum.TimeMaterials,
            label: t`Time & Materials`,
          },
        ],
      }),
      new Filter({
        column: ProjectsWhereColumn.QuickFilter,
        operator: SqlOperator.Eq,
        title: t`Projects`,
        hidden: true,
        options: [],
      }),
      new Filter({
        column: ProjectsWhereColumn.CategoryId,
        operator: SqlOperator.In,
        title: t`Project category`,
        options: [],
        bottomExtraContent: <ProjectCategorySelectBottomExtraContent />,
      }),
      new Filter({
        column: ProjectsWhereColumn.IsTemplate,
        operator: SqlOperator.Eq,
        options: [],
        hidden: true,
        value: "true",
      }),
    ],
    {
      onChange: () => {
        this.pagination.resetPage();
      },
      tableKey: this.storeKey,
      orderBy: this.orderBy,
    },
  );

  @observable.ref searchTerm = "";
  @observable.ref idToDelete: string | null = null;
  @observable projectTemplates: ProjectTemplateFragment[] = [];

  constructor(private appStore: AppStore) {
    makeObservable(this);
    this.fetchFilterOptions();

    this.where.fromURLSearchParam(Router.query);
    this.orderBy.fromURLSearchParam(Router.query);
    this.searchTerm = getParamFromQuery("search", Router.query) ?? "";
    this.pagination.fromURLSearchParam(Router.query);

    this.fetchTableData();
  }

  @computed get isDeleteModalOpen() {
    return Boolean(this.idToDelete);
  }

  @action.bound async fetchTableData(
    variables?: Partial<ProjectTemplatesQueryVariables>,
  ) {
    const [data, error] = await this.fetcher.fetch({
      first: variables?.first ?? this.pagination.asParams.first,
      page: variables?.page ?? this.pagination.asParams.page,
      filters: {
        search: this.searchTerm,
        where: this.where.asWhereParam,
        orderBy: this.orderBy.asWhereParam,
      },
    });

    if (error || !data.projects) return;

    this.pagination.setFromPaginatorInfo(data.projects?.paginatorInfo);
    this.projectTemplates = data.projects.data;
  }

  @action.bound async fetchFilterOptions() {
    const [data, error] = await this.filterOptionsFetcher.fetch();

    if (error) return;

    this.where.filtersByColumn
      .get(ProjectsWhereColumn.TeamId)
      ?.setOptions(mapToOptions.teams(data.teamSimpleMap));
    this.where.filtersByColumn
      .get(ProjectsWhereColumn.ProjectManagerId)
      ?.setOptions(
        mapToOptions.projectManagers.toOptions(data.projectManagerSimpleMap),
      );
    this.where.filtersByColumn
      .get(ProjectsWhereColumn.BrandId)
      ?.setOptions(mapToOptions.brandsSectioned(data.clientsSimpleMap));
  }

  @action.bound handleChangeOrderBy(clause: {
    column: ProjectOrderByColumnEnum;
    order: SortOrder | undefined;
  }) {
    if (!clause.order) {
      this.orderBy.setClauses([]);
      this.pagination.resetPage();
      return;
    }
    this.orderBy.setClauses([{ column: clause.column, order: clause.order }]);
    this.pagination.resetPage();
  }

  @action.bound handleSearch(newSearchTerm: string) {
    this.searchTerm = newSearchTerm;
    this.pagination.resetPage();
  }

  @action.bound handleDeleteModalOpen(id: string) {
    this.idToDelete = id;
  }

  @action.bound handleDeleteModalClose() {
    this.idToDelete = null;
  }

  @action.bound async handleDelete() {
    if (!this.idToDelete) {
      this.appStore.UIStore.toast({
        status: "error",
        title: t`Missing template ID.`,
      });

      captureEvent({
        message: "FE: Missing template ID to delete.",
      });
      return;
    }
    const [data, error] = await this.deleteMutator.mutate({
      id: this.idToDelete,
    });

    if (error) {
      this.appStore.UIStore.toast({
        status: "error",
      });
      return;
    }
    this.handleDeleteModalClose();
    const indexToRemove = this.projectTemplates.findIndex(
      (template) => template.id === data.deleteProject.id,
    );

    if (indexToRemove === -1) return;
    this.projectTemplates.splice(indexToRemove, 1);
  }
}
